import React, { useEffect, useState, useCallback } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import NoRecordFound from '../../../components/common/no-record-found';
import { FaFilter, FaChevronDown, FaChevronUp } from 'react-icons/fa';

const ROWS_PER_PAGE = 34;

const SalesReport = () => {
  const [data, setData] = useState([]);
  const [expandedContracts, setExpandedContracts] = useState({});
  const [expandedSalesOrders, setExpandedSalesOrders] = useState({});
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  // ✅ Added contract date filters
  const [filters, setFilters] = useState({
    contractDateFrom: '',
    contractDateTo: ''
  });

  useEffect(() => {
    const delay = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(delay);
  }, [search]);

  // ✅ Filter change handler
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setPage(1); // Reset to first page when filters change
  };

  // ✅ Reset filters function
  const resetFilters = () => {
    setFilters({
      contractDateFrom: '',
      contractDateTo: ''
    });
    setPage(1);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ 
        page, 
        limit: ROWS_PER_PAGE, 
        search: debouncedSearch.trim(),
        contractDateFrom: filters.contractDateFrom,
        contractDateTo: filters.contractDateTo
      });
      const res = await fetch(`${API_BASE_URL}/api/report/sales-report?${params.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const result = await res.json();
      setData(result.data || []);
      setTotal(result.total || result.data?.length || 0);
    } catch (err) {
      setError('Failed to load sales report');
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, filters]);

  // ✅ Updated useEffect to include filters
  useEffect(() => {
    fetchData();
  }, [debouncedSearch, page, filters, fetchData]);

  const totalPages = Math.ceil(total / ROWS_PER_PAGE);
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };
  const handlePrevPage = () => setPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => setPage((p) => Math.min(totalPages, p + 1));

  const toggleContract = (contractId) => {
    setExpandedContracts((prev) => ({ ...prev, [contractId]: !prev[contractId] }));
  };
  const toggleSalesOrder = (saleId) => {
    setExpandedSalesOrders((prev) => ({ ...prev, [saleId]: !prev[saleId] }));
  };

  return (
    <div className="max-w-8xl mx-auto p-6 bg-white shadow-sm border border-gray-100">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Sales Contract Register</h2>
          <p className="text-sm text-gray-500 mt-1">Contract-wise sales and delivery order summary</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-80">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-4 w-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" strokeWidth={2} />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search by contract, buyer, commodity..."
              value={search}
              onChange={handleSearchChange}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition"
            />
          </div>
          {/* ✅ Filter Toggle Button */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-[#10a5e9] rounded-md hover:bg-blue-100 text-sm"
          >
            <FaFilter />
            {showFilters ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}
          </button>
        </div>
      </div>

      {/* ✅ Filter Section */}
      {showFilters && (
        <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex flex-col w-48">
              <label className="text-sm font-medium text-gray-700 mb-1">Contract Date From</label>
              <input
                type="date"
                name="contractDateFrom"
                value={filters.contractDateFrom}
                onChange={handleFilterChange}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#10a5e9] focus:border-[#10a5e9] text-sm"
              />
            </div>

            <div className="flex flex-col w-48">
              <label className="text-sm font-medium text-gray-700 mb-1">Contract Date To</label>
              <input
                type="date"
                name="contractDateTo"
                value={filters.contractDateTo}
                onChange={handleFilterChange}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#10a5e9] focus:border-[#10a5e9] text-sm"
              />
            </div>

            <div className="flex gap-2 mt-1">
              <button
                onClick={resetFilters}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm"
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-12 space-y-3">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600"></div>
          <p className="text-sm text-gray-600">Loading sales data...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg">
          <h3 className="text-sm font-medium text-red-800">Error loading data</h3>
          <p className="text-sm text-red-700 mt-1">{error}</p>
        </div>
      ) : data.length === 0 ? (
        <NoRecordFound message="No sales contracts found" subMessage="Try adjusting your search or filters" iconClass="text-gray-400" />
      ) : (
        <>
          <div className="overflow-x-auto border border-gray-200 shadow-xs">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-gray-700 font-semibold">
                <tr>
                  <th className="px-4 py-2 text-left">#</th>
                  <th className="px-4 py-2 text-left">Contract No.</th>
                  <th className="px-4 py-2 text-left">Contract Date</th>
                  <th className="px-4 py-2 text-left">Buyer</th>
                  <th className="px-4 py-2 text-left">Commodity</th>
                  <th className="px-4 py-2 text-right">Contract Qty</th>
                  <th className="px-4 py-2 text-right">Sale Price</th>
                  <th className="px-4 py-2 text-left">Remarks</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {data.map((contract, idx) => (
                  <React.Fragment key={contract.contract_no}>
                    <tr
                      className="hover:bg-gray-50 cursor-pointer bg-blue-50 font-semibold"
                      onClick={() => toggleContract(contract.sales_contract_id)}
                    >
                      <td className="px-4 py-3">{expandedContracts[contract.sales_contract_id] ? '−' : '+'}</td>
                      <td className="px-4 py-3 text-[#10a5e9] font-bold">{contract.contract_no}</td>
                      <td className="px-4 py-3">{contract.contract_date}</td>
                      <td className="px-4 py-3">{contract.buyer}</td>
                      <td className="px-4 py-3">{contract.commodity}</td>
                      <td className="px-4 py-3 text-right font-mono">{contract.contract_qty}</td>
                      <td className="px-4 py-3 text-right font-mono">{contract.sale_price}</td>
                      <td className="px-4 py-3">{contract.remarks}</td>
                    </tr>
                    {expandedContracts[contract.sales_contract_id] && (
                      <tr>
                        <td colSpan={8} className="bg-gray-50 px-4 py-2">
                          <div className="font-bold text-[#10a5e9] mb-1">Sales Orders</div>
                          <table className="w-full text-xs border rounded shadow-sm">
                            <thead className="bg-white border-b text-gray-600 font-semibold">
                              <tr>
                                <th className="px-2 py-1 text-left">#</th>
                                <th className="px-2 py-1 text-left">Order Date</th>
                                <th className="px-2 py-1 text-right">Qty (MT)</th>
                                <th className="px-2 py-1 text-right">Sale Price/MT</th>
                                <th className="px-2 py-1 text-left">Remarks</th>
                              </tr>
                            </thead>
                            <tbody>
                              {contract.sales_orders && contract.sales_orders.length > 0 ? (
                                contract.sales_orders.map((so, soIdx) => (
                                  <React.Fragment key={so.sale_id}>
                                    <tr
                                      className="hover:bg-gray-50 cursor-pointer bg-green-50 font-semibold"
                                      onClick={() => toggleSalesOrder(so.sale_id)}
                                    >
                                      <td className="px-2 py-1">{expandedSalesOrders[so.sale_id] ? '−' : '+'}</td>
                                      <td className="px-2 py-1">{so.order_date}</td>
                                      <td className="px-2 py-1 text-right font-mono">{so.quantity_mt}</td>
                                      <td className="px-2 py-1 text-right font-mono">{so.sale_price_per_mt}</td>
                                      <td className="px-2 py-1">{so.remarks}</td>
                                    </tr>
                                    {expandedSalesOrders[so.sale_id] && (
                                      <tr>
                                        <td colSpan={5} className="bg-gray-100 px-2 py-2">
                                          <div className="font-bold text-green-700 mb-1">Delivery Orders</div>
                                          <table className="w-full text-xs border rounded shadow-sm">
                                            <thead className="bg-white border-b text-gray-600 font-semibold">
                                              <tr>
                                                <th className="px-2 py-1 text-left">DO No</th>
                                                <th className="px-2 py-1 text-left">DO Date</th>
                                                <th className="px-2 py-1 text-right">Qty (MT)</th>
                                                <th className="px-2 py-1 text-left">Warehouse</th>
                                                <th className="px-2 py-1 text-left">Buyer</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {so.delivery_orders && so.delivery_orders.length > 0 ? (
                                                so.delivery_orders.map((doo, doIdx) => (
                                                  <tr key={doo.do_id} className="border-b hover:bg-gray-50 bg-yellow-50 font-normal">
                                                    <td className="px-2 py-1">{doo.do_number}</td>
                                                    <td className="px-2 py-1">{doo.do_date}</td>
                                                    <td className="px-2 py-1 text-right font-mono">{doo.quantity_mt}</td>
                                                    <td className="px-2 py-1">{doo.warehouse_location}</td>
                                                    <td className="px-2 py-1">{doo.buyer_name}</td>
                                                  </tr>
                                                ))
                                              ) : (
                                                <tr>
                                                  <td colSpan={5} className="text-center text-xs text-gray-500 bg-yellow-50 py-2">No delivery orders found.</td>
                                                </tr>
                                              )}
                                            </tbody>
                                          </table>
                                        </td>
                                      </tr>
                                    )}
                                  </React.Fragment>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={5} className="text-center text-xs text-gray-500 bg-green-50 py-2">No sales orders found.</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {total > ROWS_PER_PAGE && (
            <div className="flex items-center justify-between mt-4 px-4 py-3 bg-white border-t border-gray-200 rounded-b-xl">
              <p className="text-sm text-gray-700">
                Showing {(page - 1) * ROWS_PER_PAGE + 1} to {Math.min(page * ROWS_PER_PAGE, total)} of {total} records
              </p>
              <div>
                <nav className="relative z-0 inline-flex shadow-sm rounded-md">
                  <button
                    onClick={handlePrevPage}
                    disabled={page === 1}
                    className={`px-4 py-2 border text-sm font-medium ${
                      page === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Previous
                  </button>
                  <button
                    onClick={handleNextPage}
                    disabled={page === totalPages}
                    className={`px-4 py-2 border text-sm font-medium ${
                      page === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SalesReport;
